import React from 'react'
import { motion } from 'framer-motion'
import { Github, Linkedin, Mail, Globe, ArrowUp } from 'lucide-react'
import { Profile, Social } from '../types'
import { fadeUp, viewportOnce, wiggle } from '../lib/motion'

interface FooterProps {
  profile: Profile
  socials: Social[]
}

const Footer: React.FC<FooterProps> = ({ profile, socials }) => {
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'github':
        return <Github className="h-4 w-4 sm:h-5 sm:w-5" />
      case 'linkedin':
        return <Linkedin className="h-4 w-4 sm:h-5 sm:w-5" />
      case 'mail':
        return <Mail className="h-4 w-4 sm:h-5 sm:w-5" />
      case 'globe':
        return <Globe className="h-4 w-4 sm:h-5 sm:w-5" />
      default:
        return <Globe className="h-4 w-4 sm:h-5 sm:w-5" />
    }
  }

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })

  return (
    <motion.footer
      className="bg-gray-900 dark:bg-black text-gray-400"
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      variants={fadeUp}
    >
      <div className="container-max section-padding py-8 sm:py-10 flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
        <div className="text-center sm:text-left">
          <div className="text-lg font-bold text-white">{profile.name}</div>
          <p className="text-sm">{profile.title}</p>
        </div>

        <div className="flex items-center space-x-3">
          {socials.map((social, index) => (
            <motion.a
              key={index}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
              aria-label={social.name}
              whileHover={{ scale: 1.12, ...wiggle.whileHover }}
              whileTap={{ scale: 0.95 }}
              transition={wiggle.transition}
            >
              {getIcon(social.icon)}
            </motion.a>
          ))}

          <motion.button
            onClick={scrollToTop}
            className="p-2 sm:p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full"
            aria-label="Back to top"
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.9 }}
          >
            <ArrowUp className="h-4 w-4" />
          </motion.button>
        </div>
      </div>

      <div className="border-t border-gray-800">
        <div className="container-max section-padding py-4 text-center text-xs">
          © {new Date().getFullYear()} {profile.name}. All rights reserved.
        </div>
      </div>
    </motion.footer>
  )
}

export default Footer
